import { useEffect, useState } from "react";
import {
    Box, Typography, TextField, IconButton, Rating
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import ApiService from "../../_services/ApiService";
import ReusableMuiIcons from "../../_helpers/ReusableMuiIcons";
import ReusableButton from "../../components/reusableComponents/reusableButtonComponents";


const BrandRatingPage = ({ applicationData }) => {
    const [rating, setRating] = useState(0);
    const [feedback, setFeedback] = useState("");
    const [submittedData, setSubmittedData] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const { id } = useParams();
    const [ratingError, setRatingError] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleStarClick = (index) => {
        const clickedRating = index + 1;
        if (rating === clickedRating - 0.5) {
            setRating(clickedRating);
        } else {
            setRating(clickedRating - 0.5);
        }
        setRatingError(false);
    };

    const renderStar = (index) => {
        const starValue = index + 1;

        if (rating >= starValue) {
            return <StarIcon sx={{ color: "#ffc107", fontSize: 30 }} />;
        } else if (rating >= starValue - 0.5) {
            return (
                <Box sx={{ position: "relative", width: 30, height: 30 }}>
                    <StarBorderIcon
                        sx={{
                            color: "#ddd", fontSize: 30, position: "absolute",
                            top: 0, left: 0, zIndex: 1,
                        }}
                    />
                    <Box sx={{ width: "50%", height: "100%", overflow: "hidden", position: "absolute", top: 0, left: 0, zIndex: 2, }} >
                        <StarIcon sx={{ color: "#ffc107", fontSize: 30, position: "absolute", top: 0, left: 0, }}
                        />
                    </Box>
                </Box>
            );
        } else {
            return <StarBorderIcon sx={{ color: "#ddd", fontSize: 30 }} />;
        }
    };

    const submitRating = async () => {
        if (rating <= 0) {
            setRatingError(true);
            return;
        }
        setLoading(true);
        const payload = {
            submission_id: applicationData?.id,
            rating,
            feedback: feedback.trim()
        };

        const res = await ApiService.post("submit_rating_creator", payload);
        if (res.status === 200 || res.status === 201) {
            try {
                const response = await ApiService.get("get_rating_creator", {}, applicationData?.id);
                setSubmittedData(response?.data.results[0]);
                setEditMode(false);
            } catch (err) {
                console.error("Failed to fetch submitted review", err);
            } finally {
                setLoading(false);
            }
        }
    };

    const editSubmitRating = async () => {
        if (rating <= 0) {
            setRatingError(true);
            return;
        }
        setLoading(true);
        try {
            const payload = {
                rating,
                feedback: feedback.trim()
            };
            const res = await ApiService.put("update_rating_creator", payload, null, applicationData?.id);
            if (res.status === 200 || res.status === 201) {
                const response = await ApiService.get("get_rating_creator", {}, applicationData?.id);
                setSubmittedData(response?.data.results[0]);
                setEditMode(false);
            }
        } catch (err) {
            console.error("Failed to update rating", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const getSubmitRating = async () => {
            try {
                const response = await ApiService.get("get_rating_creator", {}, applicationData?.id);
                const result = response?.data?.results?.[0];

                if ((response.status === 200 || response.status === 201) && result) {
                    setSubmittedData(result);
                }
            } catch (err) {
                console.error("Failed to fetch reviews", err);
            }
        };

        if (applicationData?.id) {
            getSubmitRating();
        }
    }, [applicationData?.id]);


    return (
        <Box sx={{ backgroundColor: "#fff", p: 3, borderRadius: 2 }}>
            {submittedData && !editMode ? (
                <>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#7A7A7A' }}>
                            Review
                        </Typography>
                        <IconButton onClick={() => {
                            setEditMode(true);
                            setRating(submittedData?.rating);
                            setFeedback(submittedData?.feedback);
                        }}>
                            <ReusableMuiIcons name="edit" />
                        </IconButton>
                    </Box>
                    <Typography sx={{ fontWeight: 400, fontSize: '16px', color: "#A6A6A6", mt: 1 }}>
                        Updated {new Date(submittedData?.created_at).toLocaleDateString()}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                        <Rating value={Number(submittedData?.rating) || 0} readOnly precision={0.1} />
                    </Box>

                    <Typography sx={{ mt: 1, fontSize: "16px", color: "#7A7A7A" }}>
                        Feedback:
                    </Typography>
                    <Box sx={{ border: "1px solid #ddd", borderRadius: '10px', mt: 0.5, p: 1, fontSize: "13px", color: "#7A7A7A" }} >
                        "{submittedData?.feedback}"
                    </Box>
                </>
            ) : (
                <>
                    <Typography sx={{ color: "#7A7A7A", fontWeight: 600, mb: 1, fontSize: '18px' }}>
                        {submittedData ? "Edit Your Review" : "Rate This Influencer"}
                    </Typography>

                    <Typography sx={{ mb: 1, color: "#7A7A7A", fontSize: '14px' }}>
                        Overall Rating
                    </Typography>

                    <Box sx={{ display: "flex", mb: 2 }}>
                        {[...Array(5)].map((_, i) => (
                            <IconButton key={i} onClick={() => handleStarClick(i)} sx={{ p: 0 }}>
                                {renderStar(i)}
                            </IconButton>
                        ))}
                    </Box>
                    {ratingError && (
                        <Typography sx={{ color: "red", fontSize: "13px", mb: 2 }}>
                            Please select a rating before submitting.
                        </Typography>
                    )}

                    <Typography sx={{ mb: 1, color: "#7A7A7A", fontSize: '14px' }}>
                        Feedback (Optional)
                    </Typography>

                    <TextField
                        multiline
                        rows={2}
                        fullWidth
                        placeholder="Share your experience working with this influencer..."
                        variant="outlined"
                        size="small"
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        sx={{ mb: 2, borderRadius: '10px' }}
                    />

                    <Box display="flex" justifyContent="flex-end">
                        <ReusableButton fullWidth children={submittedData ? "Save Changes" : "Submit Rating"} loader={loading} disabled={loading} variant="contained" classNames="gn-actionbtn" sx={{ textTransform: "none", color: "#fff" }} onClick={submittedData ? editSubmitRating : submitRating} />
                    </Box>
                </>
            )}
        </Box>
    );
};

export default BrandRatingPage;

